import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { FileText, Eye, Search, Filter } from "lucide-react";
import "../styles/Admin.css";
import { getAllApplications } from "../ServiceAPI/AdminUser/applicationapi";

const ApplicationsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Fetch applications from backend
  const fetchApplications = async () => {
    try {
      setLoading(true);
      const data = await getAllApplications();
      console.log("getAllApplications response:", data);
      
      let apps = [];
      if (Array.isArray(data)) {
        apps = data;
      } else if (Array.isArray(data.applications)) {
        apps = data.applications;
      } else if (Array.isArray(data.data)) {
        apps = data.data;
      }
      
      // Debug: Log first application to see field names
      if (apps.length > 0) {
        console.log("First application data:", apps[0]);
        console.log("Status field value:", apps[0].Status);
      }
      
      setApplications(apps);
    } catch (err) {
      console.error("Error fetching applications:", err);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [location.key]); // Re-fetch when navigation occurs

  // Check authentication
  useEffect(() => {
    const isAdminAuthenticated = localStorage.getItem("adminAuth") === "true";
    if (!isAdminAuthenticated) {
      navigate("/admin/login");
    }
  }, [navigate]);

  const handleViewApplication = (applicationId) => {
    localStorage.setItem("viewApplicationId", applicationId);
    navigate(`/admin/application/${applicationId}`);
  };

  // Filter and search applications
  const filteredApplications = applications.filter(app => {
    // Search filter
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      !searchTerm ||
      (app.ApplicationId && app.ApplicationId.toString().includes(searchLower)) ||
      (app.Username && app.Username.toLowerCase().includes(searchLower)) ||
      (app.Phone && app.Phone.toLowerCase().includes(searchLower)) ||
      (app.ServiceName && app.ServiceName.toLowerCase().includes(searchLower)) ||
      (app.ServiceType && app.ServiceType.toLowerCase().includes(searchLower));

    // Status filter - backend returns 'Status' field
    const status = (app.Status || app.ApplicationStatus || app.status || 'submitted').toLowerCase();
    const matchesStatus = statusFilter === 'all' || status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="loading-container">
          <div className="spinner-large"></div>
          <p>Loading applications...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Applications</h1>
          <p className="admin-page-subtitle">Manage all user applications</p>
        </div>
      </div>

      <div className="admin-content-area">
        <div className="data-table-card">
          <div className="data-table-header">
            <h3>Recent Applications ({filteredApplications.length} of {applications.length})</h3>
          </div>
          
          {/* Search and Filter Section */}
          <div className="search-filter-section">
            <div className="search-box">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search by ID, Username, Phone, Service..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="filter-group">
              <Filter size={18} />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Status</option>
                <option value="submitted">Submitted</option>
                <option value="documents verified">Documents Verified</option>
                <option value="payment pending">Payment Pending</option>
                <option value="payment completed">Payment Completed</option>
                <option value="in processing">In Processing</option>
                <option value="completed">Completed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
          {filteredApplications.length > 0 ? (
            <div className="table-responsive">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>Application ID</th>
                    <th>Username</th>
                    <th>Phone</th>
                    <th>Service Name</th>
                    <th>Service Type</th>
                    <th>Status</th>
                    <th>Applied Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApplications.map((app, index) => (
                    <tr key={app.ApplicationId || app.Id || index}>
                      <td>
                        <span className="td-badge">{app.ApplicationNumber || app.ApplicationId || app.Id || `APP-${index + 1}`}</span>
                      </td>
                      <td>{app.Username || app.username || 'N/A'}</td>
                      <td>{app.Phone || app.phone || 'N/A'}</td>
                      <td>{app.ServiceName || app.serviceName || 'N/A'}</td>
                      <td>{app.ServiceType || app.serviceType || 'N/A'}</td>
                      <td>
                        {(() => {
                          const statusValue = app.Status || app.ApplicationStatus || app.status || 'submitted';
                          const displayStatus = statusValue
                            .toLowerCase()
                            .split(' ')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ');
                          const statusClass = statusValue.toLowerCase().replace(/\s+/g, '-');
                          return (
                            <span className={`status-badge-modern ${statusClass}`}>
                              {displayStatus}
                            </span>
                          );
                        })()}
                      </td>
                      <td>{app.ApplicationDate || app.createdAt || app.date ? 
                          new Date(app.ApplicationDate || app.createdAt || app.date).toLocaleDateString() : 'N/A'}</td>
                      <td>
                        <button
                          className="action-btn view"
                          onClick={() => handleViewApplication(app.ApplicationId || app.Id)}
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <FileText size={48} />
              <p>No applications available.</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default ApplicationsPage;
